import { z } from 'zod';
import { fieldKeySchema, zohoTargetSchema, zohoValueTypeSchema } from './forms.js';
export const ZOHO_DATA_CENTERS = {
    com: { label: 'United States (zoho.com)', accountsServerUrl: 'https://accounts.zoho.com', apiBaseUrl: 'https://desk.zoho.com/api/v1' },
    eu: { label: 'Europe (zoho.eu)', accountsServerUrl: 'https://accounts.zoho.eu', apiBaseUrl: 'https://desk.zoho.eu/api/v1' },
    in: { label: 'India (zoho.in)', accountsServerUrl: 'https://accounts.zoho.in', apiBaseUrl: 'https://desk.zoho.in/api/v1' },
    'com.au': {
        label: 'Australia (zoho.com.au)',
        accountsServerUrl: 'https://accounts.zoho.com.au',
        apiBaseUrl: 'https://desk.zoho.com.au/api/v1',
    },
    jp: { label: 'Japan (zoho.jp)', accountsServerUrl: 'https://accounts.zoho.jp', apiBaseUrl: 'https://desk.zoho.jp/api/v1' },
    sa: { label: 'Saudi Arabia (zoho.sa)', accountsServerUrl: 'https://accounts.zoho.sa', apiBaseUrl: 'https://desk.zoho.sa/api/v1' },
    ca: {
        label: 'Canada (zohocloud.ca)',
        accountsServerUrl: 'https://accounts.zohocloud.ca',
        apiBaseUrl: 'https://desk.zohocloud.ca/api/v1',
    },
};
export const ZOHO_DATA_CENTER_KEYS = Object.keys(ZOHO_DATA_CENTERS);
export const zohoDataCenterSchema = z.enum(ZOHO_DATA_CENTER_KEYS);
/** OAuth scopes requested during "Connect to Zoho". */
export const ZOHO_OAUTH_SCOPES = [
    'Desk.tickets.ALL',
    'Desk.contacts.READ',
    'Desk.contacts.CREATE',
    'Desk.settings.READ',
    'Desk.basic.READ',
];
export const CONTACT_STRATEGIES = ['FIXED_CONTACT', 'FROM_FORM_FIELDS'];
const zohoId = z
    .string()
    .trim()
    .regex(/^\d{1,30}$/, 'Must be a numeric Zoho id');
export const updateZohoConfigRequestSchema = z
    .object({
    dataCenter: zohoDataCenterSchema,
    /** Omit to keep the stored value. */
    clientId: z.string().trim().min(5).max(200).optional(),
    /** Omit to keep the stored value. */
    clientSecret: z.string().trim().min(5).max(200).optional(),
    orgId: zohoId,
    departmentId: zohoId,
    layoutId: zohoId.nullable().default(null),
    defaultTicketStatus: z.string().trim().min(1).max(50).default('Open'),
    defaultTicketChannel: z.string().trim().min(1).max(50).default('Web'),
    contactStrategy: z.enum(CONTACT_STRATEGIES).default('FIXED_CONTACT'),
    fixedContactId: zohoId.nullable().default(null),
    contactNameFieldKey: fieldKeySchema.nullable().default(null),
    contactPhoneFieldKey: fieldKeySchema.nullable().default(null),
})
    .superRefine((v, ctx) => {
    if (v.contactStrategy === 'FIXED_CONTACT' && !v.fixedContactId) {
        ctx.addIssue({ code: 'custom', path: ['fixedContactId'], message: 'Fixed contact id is required for this strategy' });
    }
    if (v.contactStrategy === 'FROM_FORM_FIELDS' && !v.contactNameFieldKey) {
        ctx.addIssue({ code: 'custom', path: ['contactNameFieldKey'], message: 'Choose the form field that holds the contact name' });
    }
});
/**
 * Why a Zoho call failed, one list for every place that reports one:
 * - test-connection: `reasonCode` of the result, and `lastTestReasonCode` of the config;
 * - test-ticket: `error.details.reason` on the ZOHO_ERROR / PRECONDITION_FAILED response;
 * - unverified-branch alerts: `zohoUpdateErrorReasonCode` (and `zoho.errorReasonCode` of a resolution).
 * The English message stays alongside as the technical detail.
 */
export const ZOHO_FAILURE_REASONS = [
    /** Credentials, refresh token, org or department missing. */
    'ZOHO_NOT_CONFIGURED',
    /** Zoho did not answer within ZOHO_HTTP_TIMEOUT_MS. */
    'ZOHO_TIMEOUT',
    /** DNS, connection or TLS failure reaching Zoho. */
    'ZOHO_NETWORK',
    /** Zoho refused the credentials or refresh token (OAuth error, or HTTP 401). Reconnect. */
    'ZOHO_AUTH_REJECTED',
    /** HTTP 403: the connected Zoho user lacks the scope or the department/org access. */
    'ZOHO_ACCESS_DENIED',
    /** HTTP 404: the org, department, ticket or contact does not exist. */
    'ZOHO_NOT_FOUND',
    /** HTTP 429. */
    'ZOHO_RATE_LIMITED',
    /** HTTP 5xx. */
    'ZOHO_UNAVAILABLE',
    /** Any other HTTP 4xx (e.g. a field value Zoho does not accept). */
    'ZOHO_REQUEST_REJECTED',
    /** Zoho answered 2xx but not with what was expected. */
    'ZOHO_INVALID_RESPONSE',
    /** test-ticket only: the current field mappings cannot produce a ticket (details.errors). */
    'ZOHO_MAPPINGS_INVALID',
    /** Alerts only: the visit's form version maps no Zoho field for the branch. */
    'ZOHO_BRANCH_NOT_MAPPED',
    /** Anything else. */
    'ZOHO_UNEXPECTED',
];
export const zohoFailureReasonSchema = z.enum(ZOHO_FAILURE_REASONS);
export const zohoConfigDtoSchema = z.object({
    /** True when credentials, refresh token, org and department are all present. */
    configured: z.boolean(),
    dataCenter: zohoDataCenterSchema,
    accountsServerUrl: z.string(),
    apiBaseUrl: z.string(),
    orgId: z.string().nullable(),
    departmentId: z.string().nullable(),
    layoutId: z.string().nullable(),
    defaultTicketStatus: z.string(),
    defaultTicketChannel: z.string(),
    contactStrategy: z.enum(CONTACT_STRATEGIES),
    fixedContactId: z.string().nullable(),
    contactNameFieldKey: z.string().nullable(),
    contactPhoneFieldKey: z.string().nullable(),
    hasClientId: z.boolean(),
    hasClientSecret: z.boolean(),
    hasRefreshToken: z.boolean(),
    redirectUri: z.string(),
    lastTestAt: z.string().nullable(),
    lastTestOk: z.boolean().nullable(),
    lastTestMessage: z.string().nullable(),
    /** Why the last test failed; null when it passed, was never run, or predates reason codes. */
    lastTestReasonCode: zohoFailureReasonSchema.nullable().optional(),
    updatedAt: z.string().nullable(),
});
export const zohoAuthorizeUrlResponseSchema = z.object({ url: z.string(), expiresAt: z.string() });
export const zohoTestResultSchema = z.object({
    ok: z.boolean(),
    /** English; the technical detail. */
    message: z.string(),
    checkedAt: z.string(),
    /** Null when ok. Optional: older servers do not send it. */
    reasonCode: zohoFailureReasonSchema.nullable().optional(),
});
export const zohoTestTicketResponseSchema = z.object({
    ticketId: z.string(),
    ticketNumber: z.string().nullable(),
    subject: z.string(),
    sentPayload: z.record(z.string(), z.unknown()),
});
export const zohoTicketFieldDtoSchema = z.object({
    apiName: z.string(),
    label: z.string(),
    type: z.string(),
    isCustom: z.boolean(),
    isMandatory: z.boolean(),
    allowedValues: z.array(z.string()),
});
export const zohoTicketFieldsResponseSchema = z.object({
    fields: z.array(zohoTicketFieldDtoSchema),
    fetchedAt: z.string(),
    cached: z.boolean(),
});
// ---------------------------------------------------------------------------
// System (background) field mappings
// ---------------------------------------------------------------------------
export const SYSTEM_MAPPING_KEYS = [
    'EMPLOYEE_NAME',
    'START_LATITUDE',
    'START_LONGITUDE',
    'START_TIMESTAMP',
    'END_LATITUDE',
    'END_LONGITUDE',
    'END_TIMESTAMP',
    'GPS_ANOMALY_FLAG',
    'GPS_DISTANCE_METERS',
    'VISIT_DURATION_MINUTES',
    'MAPS_URL',
    // Aggregates over every project visit of the trip (keys of the options, in project order).
    'VISIT_TYPES',
    'SERVICES',
    'ISSUE_FLAG',
    'ISSUE_TYPES',
    'ISSUES',
    // The planned visit (assignment) the visit was started from, e.g. "ASG-000123"; empty when unplanned.
    'ASSIGNMENT_REFERENCE',
];
/** Aggregate keys: each reads the per-project field with this role in every block of the trip. */
export const TRIP_AGGREGATE_ROLES = {
    VISIT_TYPES: 'VISIT_TYPE',
    SERVICES: 'SERVICE',
    ISSUE_FLAG: 'ISSUE_FLAG',
    ISSUE_TYPES: 'ISSUE_TYPE',
    ISSUES: 'ISSUES',
};
export const isTripAggregateKey = (key) => Object.hasOwn(TRIP_AGGREGATE_ROLES, key);
export const SYSTEM_MAPPING_DEFINITIONS = {
    EMPLOYEE_NAME: {
        label: 'Employee / agent name',
        description: 'Name of the authenticated agent who submitted the visit',
        valueType: 'STRING',
    },
    START_LATITUDE: {
        label: 'Start latitude',
        description: 'GPS latitude captured at check-in (empty when unavailable)',
        valueType: 'NUMBER',
    },
    START_LONGITUDE: {
        label: 'Start longitude',
        description: 'GPS longitude captured at check-in (empty when unavailable)',
        valueType: 'NUMBER',
    },
    START_TIMESTAMP: { label: 'Start time', description: 'Check-in time', valueType: 'DATE_TIME' },
    END_LATITUDE: { label: 'End latitude', description: 'GPS latitude captured at check-out (empty when unavailable)', valueType: 'NUMBER' },
    END_LONGITUDE: {
        label: 'End longitude',
        description: 'GPS longitude captured at check-out (empty when unavailable)',
        valueType: 'NUMBER',
    },
    END_TIMESTAMP: { label: 'End time', description: 'Check-out time', valueType: 'DATE_TIME' },
    GPS_ANOMALY_FLAG: {
        label: 'GPS anomaly flag',
        description: 'True when the server flagged the visit as an anomaly',
        valueType: 'BOOLEAN',
    },
    GPS_DISTANCE_METERS: {
        label: 'Distance from branch (m)',
        description: 'Check-out distance from the verified branch location',
        valueType: 'NUMBER',
    },
    VISIT_DURATION_MINUTES: { label: 'Visit duration (minutes)', description: 'Check-out minus check-in, in minutes', valueType: 'NUMBER' },
    MAPS_URL: { label: 'Google Maps link', description: 'Link to the check-out location', valueType: 'STRING' },
    VISIT_TYPES: {
        label: 'Visit types (all projects)',
        description: 'Visit type of every project in the trip (option keys, distinct, main visit first)',
        valueType: 'STRING',
    },
    SERVICES: {
        label: 'Services (all projects)',
        description: 'Service of every project in the trip (option keys, distinct, main visit first)',
        valueType: 'STRING',
    },
    ISSUE_FLAG: {
        label: 'Issue found (any project)',
        description: 'True when an issue was reported for at least one project in the trip',
        valueType: 'BOOLEAN',
    },
    ISSUE_TYPES: {
        label: 'Issue types (all projects)',
        description: 'Issue type of every project with an issue (option keys, distinct)',
        valueType: 'STRING',
    },
    ISSUES: {
        label: 'Issues (all projects)',
        description: 'Every selected issue across the trip (option keys, distinct)',
        valueType: 'STRING',
    },
    ASSIGNMENT_REFERENCE: {
        label: 'Assignment reference',
        description: 'Reference of the planned visit this visit was started from (e.g. ASG-000123); empty for an unplanned visit',
        valueType: 'STRING',
    },
};
export function multiValueStrategy(zohoType) {
    if (!zohoType)
        return 'FIRST';
    const group = zohoFieldTypeGroup(zohoType);
    if (group === 'text')
        return 'JOIN';
    if (group === 'multiselect')
        return 'MULTI';
    return 'FIRST';
}
export const systemMappingDtoSchema = z.object({
    systemKey: z.enum(SYSTEM_MAPPING_KEYS),
    label: z.string(),
    description: z.string(),
    zohoTarget: zohoTargetSchema,
    zohoApiFieldName: z.string().nullable(),
    zohoValueType: zohoValueTypeSchema,
});
export const updateSystemMappingsRequestSchema = z.object({
    mappings: z
        .array(z.object({
        systemKey: z.enum(SYSTEM_MAPPING_KEYS),
        zohoTarget: zohoTargetSchema,
        zohoApiFieldName: z.string().trim().min(1).max(100).nullable(),
        zohoValueType: zohoValueTypeSchema,
    }))
        .min(1)
        .max(SYSTEM_MAPPING_KEYS.length),
});
const ZOHO_TYPE_GROUPS = {
    text: 'text',
    textarea: 'text',
    singleline: 'text',
    multiline: 'text',
    number: 'number',
    decimal: 'number',
    currency: 'number',
    percent: 'number',
    boolean: 'boolean',
    checkbox: 'boolean',
    datetime: 'datetime',
    date: 'date',
    picklist: 'picklist',
    multiselect: 'multiselect',
    phone: 'phone',
    email: 'email',
    url: 'url',
    website: 'url',
};
export function zohoFieldTypeGroup(zohoType) {
    return ZOHO_TYPE_GROUPS[zohoType.toLowerCase().replace(/[\s_-]/g, '')] ?? 'other';
}
export function checkZohoCompatibility(zohoType, fieldType, valueType) {
    const group = zohoFieldTypeGroup(zohoType);
    const expected = {
        STRING: ['text', 'picklist', 'multiselect', 'phone', 'email', 'url', 'other'],
        NUMBER: ['number', 'text'],
        BOOLEAN: ['boolean'],
        DATE_TIME: ['datetime', 'date', 'text'],
    };
    if (!expected[valueType].includes(group)) {
        return { level: 'INCOMPATIBLE', hint: `Zoho field type "${zohoType}" does not accept ${valueType} values` };
    }
    if (group === 'picklist' && fieldType !== 'SELECT' && fieldType !== 'BRAND_SELECT' && fieldType !== 'BRANCH_SELECT') {
        return { level: 'WARN', hint: 'Zoho picklist only accepts its allowed values; use a Select field with matching options' };
    }
    if (group === 'multiselect' && fieldType !== 'MULTI_SELECT') {
        return { level: 'WARN', hint: 'Zoho multi-select expects values from its allowed list' };
    }
    if (group === 'text' && valueType !== 'STRING') {
        return { level: 'WARN', hint: 'Value will be sent to a text field' };
    }
    return { level: 'OK', hint: null };
}
//# sourceMappingURL=zoho.js.map